import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthService } from '../auth.service';

@Component({
  selector: 'pms-change-password-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  template: `
    <h2 mat-dialog-title class="!flex items-center gap-2">
      <mat-icon class="text-[#1B4F8C]">lock_reset</mat-icon>
      Cambiar mi contraseña
    </h2>

    <mat-dialog-content>
      <form [formGroup]="form" class="flex flex-col gap-3 min-w-[340px]">
        <mat-form-field appearance="outline">
          <mat-label>Contraseña actual</mat-label>
          <input matInput [type]="showCurrent ? 'text' : 'password'" formControlName="currentPassword" autocomplete="current-password">
          <button mat-icon-button matSuffix type="button" (click)="showCurrent = !showCurrent">
            <mat-icon>{{ showCurrent ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
          <mat-error *ngIf="form.get('currentPassword')?.hasError('required')">Requerida</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Nueva contraseña</mat-label>
          <input matInput [type]="showNew ? 'text' : 'password'" formControlName="newPassword" autocomplete="new-password">
          <button mat-icon-button matSuffix type="button" (click)="showNew = !showNew">
            <mat-icon>{{ showNew ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
          <mat-hint>Mínimo 8 caracteres</mat-hint>
          <mat-error *ngIf="form.get('newPassword')?.hasError('minlength')">Mínimo 8 caracteres</mat-error>
          <mat-error *ngIf="form.get('newPassword')?.hasError('required')">Requerida</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Confirmar nueva contraseña</mat-label>
          <input matInput [type]="showNew ? 'text' : 'password'" formControlName="confirmPassword" autocomplete="new-password">
          <mat-error *ngIf="form.hasError('mismatch') && !form.get('confirmPassword')?.pristine">
            Las contraseñas no coinciden
          </mat-error>
        </mat-form-field>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close [disabled]="saving">Cancelar</button>
      <button mat-raised-button color="primary" (click)="submit()" [disabled]="form.invalid || saving">
        <mat-spinner *ngIf="saving" diameter="18" class="inline-block mr-2"></mat-spinner>
        {{ saving ? 'Guardando...' : 'Cambiar contraseña' }}
      </button>
    </mat-dialog-actions>
  `,
})
export class ChangePasswordDialogComponent {
  showCurrent = false;
  showNew = false;
  saving = false;

  form = this.fb.group(
    {
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    },
    { validators: this.matchValidator }
  );

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private authService: AuthService,
    private snackbar: MatSnackBar
  ) {}

  private matchValidator(group: any) {
    const nw = group.get('newPassword')?.value;
    const cf = group.get('confirmPassword')?.value;
    return nw && cf && nw !== cf ? { mismatch: true } : null;
  }

  submit(): void {
    if (this.form.invalid) return;
    this.saving = true;
    const { currentPassword, newPassword } = this.form.value;
    this.authService.changeOwnPassword(currentPassword!, newPassword!).subscribe({
      next: (res) => {
        this.snackbar.open(res.message || 'Contraseña actualizada', 'OK', { duration: 4000 });
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.error?.detail || err?.userMessage || 'Error al cambiar contraseña';
        this.snackbar.open(msg, 'OK', { duration: 6000 });
      },
    });
  }
}
